import React, { useMemo, useState } from "react";
import { Dropdown } from "@components";
import { RegistrationShell } from "../components/RegistrationShell";
import { useGeniusRegistration } from "../GeniusRegistrationContext";
import { useGeniusGeo } from "../useGeniusGeo";
import { INDIA_ISD, SCHOOL_ROLE_OPTIONS } from "../constants";
import { validateEmail, validateName } from "../validations";
import { useGeniusLeadCapture } from "../useGeniusLeadCapture";
import styles from "../GeniusRegistrationModal.module.css";

const sanitizeNameInput = (value) =>
  value
    .replace(/[^a-zA-Z\s]/g, "")
    .replace(/^\s+/, "")
    .replace(/\s{2,}/g, " ");

export const SchoolFormStep = () => {
  const { shared, goToSuccess, closeRegistration } = useGeniusRegistration();
  const { states, cities, loadingStates, loadingCities, loadCitiesForState } = useGeniusGeo();
  const { submitSchoolRegistrationLead } = useGeniusLeadCapture();
  const [submitting, setSubmitting] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [stateName, setStateName] = useState("");
  const [city, setCity] = useState("");
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [emailTouched, setEmailTouched] = useState(false);

  const cityOptions = cities;

  const handleStateChange = (val) => {
    setStateName(val);
    setCity("");
    loadCitiesForState(val);
  };

  const isFormValid = useMemo(() => {
    return (
      validateName(firstName, { required: true }).valid &&
      validateName(lastName, { required: false }).valid &&
      validateEmail(email).valid &&
      Boolean(role) &&
      schoolName.trim().length > 1 &&
      Boolean(stateName) &&
      Boolean(city)
    );
  }, [firstName, lastName, email, role, schoolName, stateName, city]);

  const handleEmailBlur = () => {
    setEmailTouched(true);
    const em = validateEmail(email);
    setFieldErrors((prev) => ({
      ...prev,
      email: em.valid ? undefined : em.message
    }));
  };

  const handleEmailChange = (e) => {
    const nextEmail = e.target.value.replace(/\s/g, "");
    setEmail(nextEmail);
    if (emailTouched) {
      const em = validateEmail(nextEmail);
      setFieldErrors((prev) => ({
        ...prev,
        email: em.valid ? undefined : em.message
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    const errors = {};
    const fn = validateName(firstName, { required: true });
    const ln = validateName(lastName, { required: false });
    const em = validateEmail(email);
    if (!fn.valid) errors.firstName = fn.message;
    if (!ln.valid) errors.lastName = ln.message;
    if (!em.valid) errors.email = em.message;
    if (!role) errors.role = "Please select your role";
    if (!schoolName.trim()) errors.schoolName = "School name is required";
    if (!stateName) errors.state = "Please select state";
    if (!city) errors.city = "Please select city";
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    const fullName = [fn.normalized, ln.normalized].filter(Boolean).join(" ");

    setSubmitting(true);
    try {
      const lsqResult = await submitSchoolRegistrationLead({
        phone: shared.mobile,
        firstName: fn.normalized,
        lastName: ln.normalized || "",
        email: em.normalized,
        role,
        name: fullName,
        school: schoolName.trim(),
        city,
        state: stateName
      });

      if (!lsqResult.success) {
        setFormError("Could not submit your details. Please try again.");
        return;
      }

      goToSuccess();
    } catch {
      setFormError("Could not submit your details. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RegistrationShell title="Registration Form" onClose={closeRegistration} wide centerTitle>
      <form className={styles.body} onSubmit={handleSubmit} noValidate>
        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label className={`${styles.label} ${styles.required}`}>First name</label>
            <input
              className={`${styles.input} ${fieldErrors.firstName ? styles.inputError : ""}`}
              value={firstName}
              onChange={(e) => setFirstName(sanitizeNameInput(e.target.value))}
              placeholder="Enter first name"
            />
            {fieldErrors.firstName ? <p className={styles.errorText}>{fieldErrors.firstName}</p> : null}
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Last name</label>
            <input
              className={`${styles.input} ${fieldErrors.lastName ? styles.inputError : ""}`}
              value={lastName}
              onChange={(e) => setLastName(sanitizeNameInput(e.target.value))}
              placeholder="Enter last name"
            />
            {fieldErrors.lastName ? <p className={styles.errorText}>{fieldErrors.lastName}</p> : null}
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Phone number</label>
          <div className={styles.phoneLockedBar}>
            <span>
              {INDIA_ISD} {shared.mobile}
            </span>
          </div>
        </div>

        <div className={styles.field}>
          <label className={`${styles.label} ${styles.required}`}>Email ID</label>
          <input
            type="email"
            className={`${styles.input} ${fieldErrors.email ? styles.inputError : ""}`}
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            placeholder="Enter email"
          />
          {fieldErrors.email ? <p className={styles.errorText}>{fieldErrors.email}</p> : null}
        </div>

        <div className={styles.field}>
          <Dropdown
            label="Role"
            placeholder="Select role"
            options={SCHOOL_ROLE_OPTIONS}
            value={role}
            onChange={setRole}
            error={fieldErrors.role}
            searchable={false}
            size="compact"
          />
        </div>

        <div className={styles.field}>
          <label className={`${styles.label} ${styles.required}`}>Enter your school name</label>
          <input
            className={`${styles.input} ${fieldErrors.schoolName ? styles.inputError : ""}`}
            value={schoolName}
            onChange={(e) => setSchoolName(e.target.value)}
            placeholder="Enter school name"
          />
          {fieldErrors.schoolName ? <p className={styles.errorText}>{fieldErrors.schoolName}</p> : null}
        </div>

        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <Dropdown
              label="State"
              placeholder={loadingStates ? "Loading..." : "Select state"}
              options={states}
              value={stateName}
              onChange={handleStateChange}
              disabled={loadingStates}
              error={fieldErrors.state}
              searchable
              size="compact"
            />
          </div>
          <div className={styles.field}>
            <Dropdown
              label="City"
              placeholder={loadingCities ? "Loading..." : "Select city"}
              options={cityOptions}
              value={city}
              onChange={setCity}
              disabled={!stateName || loadingCities}
              error={fieldErrors.city}
              searchable
              size="compact"
            />
          </div>
        </div>

        {formError ? <p className={styles.errorText}>{formError}</p> : null}

        <button
          type="submit"
          className={`${styles.primaryBtn} ${
            !isFormValid || submitting ? styles.primaryBtnDisabled : styles.primaryBtnActive
          }`}
          disabled={!isFormValid || submitting}
        >
          {submitting ? "Submitting..." : "Submit"}
        </button>
      </form>
    </RegistrationShell>
  );
};
